import type { MemoSummary } from "@/lib/types";
import { STATUS_PALETTE, type StatusColor, type StatusTint } from "@/lib/tokens";
import { t } from "@/lib/strings";

// Pure grouping + labelling for a Besprechungsreihe's protocol list (SeriesPage).
// All date logic works on the ISO day strings ("YYYY-MM-DD") the API returns and a
// local "today" key from `localDayKey` — never on Date objects parsed from the ISO
// string, which would read back the wrong day across the UTC boundary.

/**
 * One fixed hue per month (Jan … Dez), the same in every series and every year so a
 * month's colour becomes recognisable. Taken from STATUS_PALETTE (dark mode already
 * solved there) and ordered so that NEIGHBOURING months — Dez → Jan included —
 * always differ clearly; a seasonal gradient would blur exactly the boundary the
 * colour is meant to show.
 */
export const MONTH_COLORS: readonly StatusColor[] = [
  "blue",
  "purple",
  "lime",
  "pink",
  "green",
  "yellow",
  "cyan",
  "orange",
  "indigo",
  "brown",
  "magenta",
  "teal",
];

/** The month tint for a meeting date (month read from the string, no Date parsing). */
export function monthTint(meetingDate: string): StatusTint {
  return tintForMonth(Number(meetingDate.slice(5, 7)));
}

/** The tint for a calendar month, 1 (Jan) … 12 (Dez). */
export function tintForMonth(month: number): StatusTint {
  return STATUS_PALETTE[MONTH_COLORS[month - 1] ?? "slate"];
}

function dayOf(memo: MemoSummary): string {
  return memo.meeting_date.slice(0, 10);
}

// Equal dates must still come back in ONE order on every render (the list is
// re-fetched): tie-break on the id — uuid7, so creation order.
function byDateThenId(a: MemoSummary, b: MemoSummary): number {
  const da = dayOf(a);
  const db = dayOf(b);
  if (da !== db) return da < db ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** The viewer's local wall clock: day "YYYY-MM-DD" and time "HH:MM". */
export interface LocalNow {
  day: string;
  time: string;
}

/**
 * Has this meeting already happened (or begun)? Earlier days: yes. Later days: no.
 * TODAY: once it is finalized, or once its start time has passed — the memo's own
 * `start_time`, else the series' default. A 10:00 meeting is not "anstehend" at
 * 18:41. With no time known at all, today's meeting stays upcoming until it is
 * finalized or the day ends.
 */
export function hasMeetingStarted(
  memo: MemoSummary,
  now: LocalNow,
  fallbackStartTime?: string | null,
): boolean {
  const day = dayOf(memo);
  if (day !== now.day) return day < now.day;
  if (memo.status === "finalized") return true;
  const start = (memo.start_time ?? fallbackStartTime ?? "").slice(0, 5);
  return start !== "" && start <= now.time;
}

export interface SplitMemos {
  /** Meetings that have not started yet, nearest first. */
  upcoming: MemoSummary[];
  /** Meetings that have started or are over, newest first. */
  past: MemoSummary[];
}

/** Split into "Anstehend" and "Vergangen" — see `hasMeetingStarted` for the rule. */
export function splitMemosByDate(
  memos: readonly MemoSummary[],
  now: LocalNow,
  fallbackStartTime?: string | null,
): SplitMemos {
  const started = (m: MemoSummary) => hasMeetingStarted(m, now, fallbackStartTime);
  const upcoming = memos.filter((m) => !started(m)).sort(byDateThenId);
  const past = memos.filter(started).sort((a, b) => byDateThenId(b, a));
  return { upcoming, past };
}

export interface YearGroup {
  year: string;
  memos: MemoSummary[];
}

/** Group by calendar year, newest year first; each group keeps the input order. */
export function groupByYear(memos: readonly MemoSummary[]): YearGroup[] {
  const groups = new Map<string, MemoSummary[]>();
  for (const m of memos) {
    const year = dayOf(m).slice(0, 4);
    const list = groups.get(year);
    if (list) list.push(m);
    else groups.set(year, [m]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([year, list]) => ({ year, memos: list }));
}

/** Months (1–12) that occur in a group, in calendar order. */
export function monthsPresent(memos: readonly MemoSummary[]): number[] {
  const set = new Set(memos.map((m) => Number(dayOf(m).slice(5, 7))));
  return [...set].sort((a, b) => a - b);
}

function utcDay(key: string): number {
  const [y, m, d] = key.split("-").map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** "Heute" / "Morgen" / "in 6 Tagen" for an upcoming meeting; null for a past one. */
export function relativeMeetingLabel(meetingDate: string, todayKey: string): string | null {
  const days = Math.round((utcDay(meetingDate.slice(0, 10)) - utcDay(todayKey)) / 86_400_000);
  if (days < 0) return null;
  if (days === 0) return t.today;
  if (days === 1) return t.tomorrow;
  return `${t.in} ${days} ${t.daysDative}`;
}

/**
 * Is there a Protokoll that board comments can land in right now? Mirrors the backend
 * `memo_updates.target_memo_for`: a NOT-finalized memo dated today or later — by DATE
 * only. This deliberately ignores the start-time split: today's 10:00 draft sits under
 * "Vergangen" by 18:00 yet still takes comments, and a meeting finalized this morning
 * takes none. The "no next Protokoll" hint must follow the backend, not the display.
 */
export function hasCommentTarget(memos: readonly MemoSummary[], todayKey: string): boolean {
  return memos.some((m) => m.status !== "finalized" && dayOf(m) >= todayKey);
}

/**
 * A meeting from an EARLIER day whose Protokoll was never finalized — it needs
 * someone's attention. Today's draft is not flagged even once the meeting has
 * started: writing it up the same day is normal.
 */
export function isUnfinalizedPast(memo: MemoSummary, todayKey: string): boolean {
  return memo.status !== "finalized" && dayOf(memo) < todayKey;
}

/**
 * Whether a year group is open: an explicit choice wins, otherwise only the newest
 * year is open. Older years collapse to one line so a series running for years
 * stays short.
 */
export function isYearOpen(
  stored: Readonly<Record<string, boolean>>,
  year: string,
  isNewest: boolean,
): boolean {
  return stored[year] ?? isNewest;
}

const YEARS_KEY = (seriesId: string) => `flow.series.years.${seriesId}`;

/** Per-viewer open/closed year choices for one series (localStorage; never throws). */
export function loadYearOpenState(seriesId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(YEARS_KEY(seriesId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (/^\d{4}$/.test(k) && typeof v === "boolean") out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveYearOpenState(seriesId: string, state: Record<string, boolean>): void {
  try {
    localStorage.setItem(YEARS_KEY(seriesId), JSON.stringify(state));
  } catch {
    // Private mode / quota: the choice just isn't remembered.
  }
}
